// Signage → Ayuda (/signage/ayuda). Patrón ayuda-de-modulo: checklist EN VIVO
// "listo para operar" (RPC sig_ayuda_estado) + flujo + ¿cómo hago…? + glosario,
// con deep-links a cada pantalla del módulo.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, ChevronRight, Copy, Download, LifeBuoy, Link2, Loader2, MonitorSmartphone, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useTabsStore } from '@/stores/tabs.store';
import { sigRpc } from './shared';

interface AyudaData {
  checklist: Record<string, boolean | number>;
  kpis: { pantallas: number; online: number; plantillas: number; temas: number; campana_hoy: string | null; repro_24h: number };
}

function useIr() {
  const openTab = useTabsStore((s) => s.openTab);
  return (path: string, title: string) => openTab({ id: path, title, path });
}

const CHECKS: { key: string; label: string; ruta: string; titulo: string; ayuda: string; advisory?: boolean }[] = [
  { key: 'tema', label: 'Tema (brand kit) por defecto', ruta: '/signage/temas', titulo: 'Temas', ayuda: 'Paleta y logo que visten el reproductor. Ya vienen 3 (Cálido/Frío/Oscuro); marca uno por defecto.' },
  { key: 'plantilla', label: 'Al menos una plantilla activa', ruta: '/signage/plantillas', titulo: 'Plantillas', ayuda: 'La playlist de escenas (catálogo, carta, mensaje) que se reproduce en bucle.' },
  { key: 'pantalla', label: 'Al menos una pantalla creada', ruta: '/signage/pantallas', titulo: 'Pantallas', ayuda: 'Cada pantalla tiene su URL/token para abrir en el box Android.' },
  { key: 'catalogo_fotos', label: 'Productos con foto', ruta: '/pos/rest-fotos', titulo: 'Fotos del menú', ayuda: 'El catálogo se ve mucho más atractivo con foto por producto (la foto sangrada carga la pantalla).', advisory: true },
  { key: 'programacion', label: 'Programación (dayparting)', ruta: '/signage/programacion', titulo: 'Programación', ayuda: 'Opcional: cambia la plantilla por franja horaria (p. ej. la carta al mediodía).', advisory: true },
  { key: 'campanas', label: 'Campañas estacionales activas', ruta: '/signage/campanas', titulo: 'Campañas', ayuda: 'Opcional: capas de Navidad, Día de la Madre, Black Friday… que se activan solas por fecha.', advisory: true },
  { key: 'social', label: 'Redes sociales conectadas', ruta: '/signage/social', titulo: 'Redes sociales', ayuda: 'Opcional: posts de Instagram/Facebook/TikTok/X para la escena de muro social (curado o por API).', advisory: true },
  { key: 'buzon', label: 'Buzón externo (agencias/proveedores)', ruta: '/signage/buzon', titulo: 'Buzón externo', ayuda: 'Opcional: carpetas Drive/Dropbox vigiladas cuyo contenido pasa por moderación antes de publicarse.', advisory: true },
  { key: 'worker', label: 'Worker de contenidos activo', ruta: '/signage/dashboard', titulo: 'Resumen', ayuda: 'De él dependen noticias RSS, sync de redes, buzón externo y videos subidos. Si está apagado, TI debe arrancar el servicio "notificador".', advisory: true },
];

const FLUJO = [
  { n: 1, titulo: 'Ajusta el tema (marca)', detalle: 'Paleta y logo. Usa un preset o crea el tuyo; marca uno por defecto.', ruta: '/signage/temas', tituloTab: 'Temas' },
  { n: 2, titulo: 'Arma y PUBLICA una plantilla', detalle: 'Ordena las escenas (catálogo, carta, mensaje, imagen, video/playlist/TV en vivo, QR, feed, más vendidos, lo más llevado hoy, combos, por vencer, salud del día, cross-sell, redes sociales, reseñas, turnos, lentos), su duración, transición y condición de clima; previsualiza en vivo. Puedes empezar "Desde rubro". Pulsa Publicar para que salga al aire (queda versionada; se puede revertir).', ruta: '/signage/plantillas', tituloTab: 'Plantillas' },
  { n: 3, titulo: 'Agrupa y programa (opcional)', detalle: 'Agrupa pantallas por zona/ciudad y define qué plantilla corre por franja horaria.', ruta: '/signage/programacion', tituloTab: 'Programación' },
  { n: 4, titulo: 'Crea la pantalla', detalle: 'Asígnale local, plantilla, orientación (horizontal o vertical 9:16) y (si quieres) tema/grupo. Queda con una URL única (su token).', ruta: '/signage/pantallas', tituloTab: 'Pantallas' },
  { n: 5, titulo: 'Abre el reproductor en el box', detalle: 'Copia la URL en el navegador del box Android en modo kiosko —o vincúlalo por CÓDIGO en Emparejar sin copiar el token—. En segundos aparece "En línea".', ruta: '/signage/pantallas', tituloTab: 'Pantallas' },
  { n: 6, titulo: 'Activa campañas (opcional)', detalle: 'Enciende Navidad, Día de la Madre, Black Friday… se montan solas sobre el catálogo cuando llega su fecha.', ruta: '/signage/campanas', tituloTab: 'Campañas' },
  { n: 7, titulo: 'Mide en el Resumen', detalle: 'Estado de pantallas y proof-of-play: qué se mostró, cuánto y en cuáles.', ruta: '/signage/dashboard', tituloTab: 'Resumen' },
];

// Módulos avanzados (más allá del flujo básico) con enlace directo.
const AVANZADO: { t: string; d: string; ruta: string; tab: string }[] = [
  { t: 'Biblioteca de medios', d: 'Sube imágenes (WebP, punto focal, "sin fondo" con recorte automático) y videos (propios faststart, o por URL); reutilízalos en escenas.', ruta: '/signage/medios', tab: 'Biblioteca' },
  { t: 'Marco roto (efecto 3D)', d: 'Escena donde el producto "rompe" un marco dibujado y parece salirse de la pantalla; el fondo de la foto se recorta solo.', ruta: '/signage/plantillas', tab: 'Plantillas' },
  { t: 'Plantillas por rubro', d: 'Arranca con una plantilla lista para tu giro (farmacia, restaurante, moda…).', ruta: '/signage/plantillas', tab: 'Plantillas' },
  { t: 'Reglas de realce', d: 'Etiquetas OFERTA / HORA FELIZ sobre productos; precio final por local.', ruta: '/signage/reglas', tab: 'Reglas de realce' },
  { t: 'Combos de temporada', d: 'Kits/packs curados (productos reales + descuento del pack) para la escena "Combo".', ruta: '/signage/combos', tab: 'Combos' },
  { t: 'Salud del día', d: 'Consejos de salud (con clima/UV) que sugieren un producto; para la escena "Salud del día".', ruta: '/signage/salud', tab: 'Salud del día' },
  { t: 'Redes sociales', d: 'Muro de posts (Instagram/Facebook/TikTok/X) para la escena "Redes sociales": curado a mano o sincronizado por la API de Meta.', ruta: '/signage/social', tab: 'Redes sociales' },
  { t: 'Buzón externo', d: 'Carpetas Drive/Dropbox de agencias y proveedores: lo nuevo cae en moderación y solo lo aprobado llega a la Biblioteca/pantallas.', ruta: '/signage/buzon', tab: 'Buzón externo' },
  { t: 'QR phygital', d: 'QR para comprar en línea el producto exhibido o abrir la tienda/monedero.', ruta: '/signage/phygital', tab: 'QR phygital' },
  { t: 'Retail media', d: 'Vende espacio a marcas: anuncios con aprobación, frecuencia y reporte de impresiones.', ruta: '/signage/anuncios', tab: 'Retail media' },
  { t: 'Anunciantes', d: 'Marcas que pautan (cada una vinculada a un cliente para poder facturarle).', ruta: '/signage/anunciantes', tab: 'Anunciantes' },
  { t: 'Venta de franjas (Contratos)', d: 'Vende una franja horaria completa a un anunciante (su plantilla manda esa hora) y factúrasela al SRI.', ruta: '/signage/contratos', tab: 'Contratos' },
  { t: 'Feeds RSS', d: 'Fuentes de noticias que el sistema descarga para la escena de noticias.', ruta: '/signage/feeds', tab: 'Feeds RSS' },
  { t: 'Impacto en ventas', d: 'Uplift: cruza lo exhibido con las ventas del local por producto.', ruta: '/signage/uplift', tab: 'Impacto en ventas' },
  { t: 'A/B testing', d: 'Compara dos plantillas por día en un grupo y mide impresiones y ventas.', ruta: '/signage/experimentos', tab: 'A/B testing' },
  { t: 'Alertas de caída', d: 'Aviso por correo cuando una pantalla deja de reportar (+ recuperación).', ruta: '/signage/alertas', tab: 'Alertas' },
  { t: 'Emparejar / flota', d: 'Vincula el box por código y contrólalo en remoto (refrescar/recargar/limpiar caché).', ruta: '/signage/pair', tab: 'Emparejar' },
];

const COMO: { q: string; pasos: string[]; enlaces: { to: string; t: string }[] }[] = [
  {
    q: '¿Cómo pongo una pantalla nueva a funcionar?',
    pasos: [
      'En Pantallas → "Nueva pantalla": nombre, local y plantilla (y tema/grupo opcionales). Guarda.',
      'En su fila, pulsa "copiar URL" o el ícono de abrir: es la URL del reproductor (contiene el token).',
      'En el box Android, abre esa URL en el navegador en modo kiosko (p. ej. Fully Kiosk) con arranque automático.',
      'A los ~30 s la fila muestra "En línea" y empieza a reportar proof-of-play.',
    ],
    enlaces: [{ to: '/signage/pantallas', t: 'Pantallas' }],
  },
  {
    q: '¿Cómo cambio QUÉ se muestra y A QUÉ HORA?',
    pasos: [
      'QUÉ: en Plantillas edita la secuencia de escenas y su duración (con preview en vivo).',
      'CUÁNDO: en Programación crea reglas por pantalla o grupo, día y franja (mayor prioridad gana; una regla de pantalla vence a la de grupo).',
      'Sin regla vigente, la pantalla usa su plantilla por defecto todo el día.',
    ],
    enlaces: [{ to: '/signage/plantillas', t: 'Plantillas' }, { to: '/signage/programacion', t: 'Programación' }],
  },
  {
    q: '¿Cómo hago que se vea muy visual?',
    pasos: [
      'Sube la FOTO de cada producto: el reproductor la muestra a sangre con el precio como titular.',
      'Elige un tema acorde al negocio (Cálido para comida, Frío para farmacia, Oscuro premium).',
      'El stock sale solo: "Agotado" atenúa la foto y "Últimas N" marca urgencia.',
    ],
    enlaces: [{ to: '/pos/rest-fotos', t: 'Fotos del menú' }, { to: '/signage/temas', t: 'Temas' }],
  },
  {
    q: '¿Cómo activo una campaña estacional?',
    pasos: [
      'En Campañas, enciende el preset (Navidad, Día de la Madre, Black Friday…). Se activa SOLA en su rango de fechas.',
      'Segmenta por ámbito: toda la empresa, un grupo o una pantalla (p. ej. fiestas de una ciudad, regreso a clases por región).',
      'El acento (color del efecto) es configurable por campaña; el catálogo sigue vendiendo debajo.',
    ],
    enlaces: [{ to: '/signage/campanas', t: 'Campañas' }],
  },
  {
    q: '¿Cómo publico cambios sin romper lo que está al aire?',
    pasos: [
      'Edita la plantilla y pulsa "Publicar": esa versión sale al aire y las pantallas la toman en segundos.',
      'Cada publicación crea una VERSIÓN (con historial). Si algo sale mal, "Revertir" vuelve a una versión anterior.',
      'Mientras editas sin publicar, las pantallas siguen mostrando la última versión publicada.',
    ],
    enlaces: [{ to: '/signage/plantillas', t: 'Plantillas' }],
  },
  {
    q: '¿Cómo agrego video, imagen o un widget (clima/noticias)?',
    pasos: [
      'En Plantillas agrega una escena y elige el tipo: Video (archivo propio, YouTube o Vimeo), Imagen a pantalla completa, o Feed (reloj, clima o noticias).',
      'Video/Imagen: elígelos de la Biblioteca de medios (o pega una URL). Los videos subidos (o llegados por el Buzón) se optimizan solos: el sistema los convierte a H.264 con arranque rápido y les saca la miniatura — sube el archivo tal cual lo tengas (MOV, WebM, 4K…). Requiere ffmpeg instalado en el servidor.',
      'Noticias: primero crea una fuente en Feeds RSS; el sistema descarga los titulares solo.',
      'Publica la plantilla para que el cambio salga al aire.',
    ],
    enlaces: [{ to: '/signage/plantillas', t: 'Plantillas' }, { to: '/signage/medios', t: 'Biblioteca' }, { to: '/signage/feeds', t: 'Feeds RSS' }],
  },
  {
    q: '¿Cómo hago el efecto 3D del producto que se sale del marco?',
    pasos: [
      'En Plantillas agrega una escena tipo "Marco roto" y elige los productos a destacar (o una imagen de la Biblioteca).',
      'El fondo de la foto se recorta SOLO si es foto de estudio (fondo blanco/claro uniforme): el producto arranca dentro del marco, lo "rompe" y levita con sombra — la ilusión 3D. Una foto ambientada se muestra enmarcada, sin efecto.',
      'Con productos del catálogo, el precio y el stock salen EN VIVO del local (si cambia el precio, la pantalla cambia sola).',
      'Para artes de marca: en Biblioteca usa "Subir sin fondo" (o sube un PNG ya recortado) y elígelo en la escena. El color del marco es configurable.',
    ],
    enlaces: [{ to: '/signage/plantillas', t: 'Plantillas' }, { to: '/signage/medios', t: 'Biblioteca' }],
  },
  {
    q: '¿Cómo muestro combos, productos por vencer o consejos de salud?',
    pasos: [
      'COMBOS: en "Combos de temporada" arma kits (productos reales + descuento del pack, vigencia por fecha); en Plantillas agrega una escena tipo "Combo".',
      'POR VENCER: agrega una escena tipo "Por vencer" — sale solo, con los lotes próximos a caducar de ESE local (días restantes + liquidación sugerida por urgencia). No requiere configuración.',
      'SALUD DEL DÍA: en "Salud del día" carga consejos (con condición de clima opcional y producto sugerido); agrega una escena tipo "Salud del día" — el reproductor lee el clima/UV del local y muestra el consejo que corresponde.',
      'Publica la plantilla para que el cambio salga al aire.',
    ],
    enlaces: [{ to: '/signage/combos', t: 'Combos' }, { to: '/signage/salud', t: 'Salud del día' }, { to: '/signage/plantillas', t: 'Plantillas' }],
  },
  {
    q: '¿Cómo destaco precios y ofertas?',
    pasos: [
      'El precio que se muestra es el FINAL por local (aplica el descuento del POS de esa bodega).',
      'En Reglas de realce crea etiquetas (OFERTA, HORA FELIZ, ÚLTIMAS UNIDADES) que se pintan sobre el producto según condición: siempre, stock bajo o franja horaria.',
    ],
    enlaces: [{ to: '/signage/reglas', t: 'Reglas de realce' }],
  },
  {
    q: '¿Cómo hago un happy hour con precio real (no solo etiqueta)?',
    pasos: [
      'El descuento con hora vive en el POS/Inventario, no en la cartelera — así la caja cobra EXACTAMENTE lo que la pantalla promete.',
      'Precio rebajado por horas: en Inventario → Productos → Descuentos pon el Promo % con su hora de inicio/fin y días (p. ej. 20% de 14:00 a 16:00, L–V). La pantalla pinta ese precio final sola.',
      'Combos/2x1/regalo por horas: en POS → Promociones dale a la promoción su franja horaria, días o local.',
      'Al abrir o cerrar la ventana, las pantallas se actualizan solas en ~20 segundos.',
      'Remata el gancho visual con una Regla de realce "HORA FELIZ" en la misma franja.',
    ],
    enlaces: [{ to: '/signage/reglas', t: 'Reglas de realce' }, { to: '/signage/dashboard', t: 'Resumen (validar precios)' }],
  },
  {
    q: '¿Cómo llevo al cliente a comprar en línea (QR)?',
    pasos: [
      'En QR phygital actívalo y elige el destino: tu tienda en línea o una URL propia (promo/monedero).',
      'Con "mini-QR por producto", sobre cada producto exhibido aparece un QR para comprarlo online (si está publicado en la tienda).',
      'Agrega además una escena QR a pantalla completa para invitar a escanear.',
    ],
    enlaces: [{ to: '/signage/phygital', t: 'QR phygital' }],
  },
  {
    q: '¿Cómo vendo espacio publicitario a marcas (retail media)?',
    pasos: [
      'En Retail media crea el anuncio (banner o mensaje), su vigencia, alcance (empresa/grupo/pantalla) y frecuencia (1 cada N escenas).',
      'Envíalo a aprobación; un administrador lo aprueba para que salga al aire.',
      'El reporte muestra las impresiones por anuncio (para facturar al anunciante).',
    ],
    enlaces: [{ to: '/signage/anuncios', t: 'Retail media' }],
  },
  {
    q: '¿Cómo le vendo una franja horaria a una marca y se la facturo?',
    pasos: [
      'En Anunciantes crea la marca y vincúlala a un cliente (para poder emitirle la factura).',
      'Arma la plantilla del anunciante (puede ser un video o banner de la marca) y publícala.',
      'En Contratos crea el contrato: anunciante, plantilla, tarifa y la(s) franja(s) horaria(s) que compra. El sistema avisa si choca con otra franja ya vendida (no se sobrevende).',
      'Actívalo: en esa franja, esa plantilla MANDA sobre la programación normal (sale la etiqueta "Publicidad").',
      'Cuando toque cobrar, pulsa "Facturar": emite una factura SRI al cliente del anunciante por la pauta del período.',
    ],
    enlaces: [{ to: '/signage/anunciantes', t: 'Anunciantes' }, { to: '/signage/contratos', t: 'Contratos' }],
  },
  {
    q: '¿Cómo saco un cupón en pantalla que se canjee en caja?',
    pasos: [
      'En Cupones crea el cupón: código corto (botón "Generar" evita letras confusas), descuento en % o $ (opcionalmente atado a un producto), vigencia CON HORA (sirve para cupones flash "hasta las 18h"), tope de canjes y local.',
      'Agrega la escena "Cupón" a la plantilla y publícala: la pantalla muestra el código gigante con cuenta regresiva y "quedan N".',
      'El cliente dice el código en caja; el cajero lo escribe en el cobro (campo Cupón) y el POS lo valida y canjea al confirmar la venta — vigencia, local y tope se verifican en el servidor, imposible sobre-canjear.',
      'El reporte al pie de Cupones muestra la atribución directa pantalla→venta: canjes, descuento otorgado y ventas generadas por local.',
    ],
    enlaces: [{ to: '/signage/cupones', t: 'Cupones' }, { to: '/signage/plantillas', t: 'Plantillas' }],
  },
  {
    q: '¿Cómo convierto una pantalla en pantalla de cliente en la caja?',
    pasos: [
      'En Pantallas edita la pantalla del punto de pago y elige Modo = "Pantalla de cliente en caja" + la caja del POS a la que mira.',
      'Con una compra en curso muestra al cliente las líneas y el total en vivo, el QR De Una al pagar y el "gracias + vuelto" al cerrar. No muestra nombre ni datos del cliente.',
      'Ociosa (sin venta en curso) reproduce la playlist normal: es inventario premium de retail media en el punto de pago.',
    ],
    enlaces: [{ to: '/signage/pantallas', t: 'Pantallas' }],
  },
  {
    q: '¿Cómo le rindo cuentas a un anunciante (dashboard de la marca)?',
    pasos: [
      'En Anunciantes pulsa el icono de barras del anunciante: verás su inversión, reproducciones y tiempo emitido reales, la audiencia dentro de sus franjas (tickets de caja) y el CPM efectivo del período.',
      'Si tiene anuncios de retail media, se compara el SOV real (medido con el proof-of-play) contra el pactado (1 cada N escenas).',
      'Los cupones ligados a sus contratos muestran canjes y ventas con atribución directa pantalla→caja.',
      'Vincula la marca del catálogo al anunciante (editar) y el dashboard suma las ventas de SUS SKUs comparadas con el período previo del mismo largo.',
    ],
    enlaces: [{ to: '/signage/anunciantes', t: 'Anunciantes' }],
  },
  {
    q: '¿Cuánto cobro por una pantalla y cómo sé qué está disponible?',
    pasos: [
      'En Tarifario y avails define las reglas de precio: $ por pantalla·día según la clasificación del local (A/B/C/D, se calcula sola por ventas), el día y la franja horaria. Gana la regla más específica.',
      'En el cotizador elige fechas y franja: verás cada pantalla LIBRE o VENDIDA (cruza los contratos confirmados), su audiencia auditable (tickets de caja por día y dentro de la franja) y la tarifa sugerida.',
      'Selecciona pantallas y obtienes la cotización: inversión total, audiencia en franja y CPM efectivo — argumentos con datos del propio POS, no estimaciones de terceros.',
      'Con "Crear contrato(s) borrador" la cotización pasa a Contratos de franja con todo pre-llenado; ahí los confirmas (corre el anti-sobreventa) y luego facturas y emites el certificado.',
    ],
    enlaces: [{ to: '/signage/tarifario', t: 'Tarifario y avails' }, { to: '/signage/contratos', t: 'Contratos' }],
  },
  {
    q: '¿Cómo certifico a un anunciante lo que realmente se emitió?',
    pasos: [
      'Abre el Reporte del contrato (Contratos → icono de barras) y pulsa "Emitir certificado".',
      'Se congela un snapshot del proof-of-play del período: reproducciones, tiempo emitido, pantallas y % de cumplimiento, con detalle por pantalla y por día.',
      'El certificado tiene un folio único y una página pública con QR: la agencia lo verifica en línea sin usuario y lo imprime a PDF desde la misma página.',
      'El snapshot es inmutable (hash de integridad): aunque el proof-of-play siga creciendo, el certificado no cambia.',
    ],
    enlaces: [{ to: '/signage/contratos', t: 'Contratos' }],
  },
  {
    q: '¿Cómo muestro recomendaciones según lo que el cliente está comprando (cross-sell)?',
    pasos: [
      'En Plantillas agrega una escena de tipo "Cross-sell" y publícala (úsala p. ej. en la franja de la tarde/noche, cerca de las cajas).',
      'La caja del POS publica el carrito en curso del local (anónimo, sin datos del cliente) mientras cobra.',
      'La pantalla muestra "también se llevan…" con productos afines (aprende de las ventas históricas del local) y se actualiza en segundos.',
      'No configuras las afinidades a mano: el sistema las recalcula solo a diario (market-basket).',
    ],
    enlaces: [{ to: '/signage/plantillas', t: 'Plantillas' }],
  },
  {
    q: '¿Cómo sé si el contenido realmente vende?',
    pasos: [
      'Impacto en ventas (uplift): cruza lo exhibido con las ventas del local por producto y día.',
      'A/B testing: corre dos plantillas alternándolas por día en un grupo y compara impresiones y ventas.',
      'Ambos se apoyan en el proof-of-play (qué se mostró, cuánto y dónde).',
    ],
    enlaces: [{ to: '/signage/uplift', t: 'Impacto en ventas' }, { to: '/signage/experimentos', t: 'A/B testing' }],
  },
  {
    q: '¿Cómo vinculo un box y lo controlo a distancia?',
    pasos: [
      'En el box abre la pantalla de emparejamiento: muestra un CÓDIGO corto.',
      'En Emparejar ingresa ese código y elige la pantalla: queda vinculado sin copiar el token.',
      'Desde ahí envías comandos remotos: refrescar contenido, recargar la app o limpiar caché.',
    ],
    enlaces: [{ to: '/signage/pair', t: 'Emparejar' }],
  },
  {
    q: '¿Cómo me entero si una pantalla se cae?',
    pasos: [
      'En Alertas define el umbral (minutos sin reportar), los correos destino y el SMTP del módulo.',
      'Si una pantalla supera el umbral sin reportar, llega un correo; cuando vuelve, otro de recuperación.',
    ],
    enlaces: [{ to: '/signage/alertas', t: 'Alertas' }],
  },
  {
    q: '¿Tengo varias pantallas pegadas (video wall) y quiero que cambien a la vez?',
    pasos: [
      'En Plantillas activa "Sincronizar pantallas" en la plantilla que comparten y publícala.',
      'Todas las pantallas con esa plantilla derivan su posición del reloj: cambian de escena en el mismo segundo (± lo que difieran los relojes de los boxes — mantenlos con hora automática/NTP).',
      'En este modo los videos se recortan a la duración de su escena (loop hasta el corte) para no romper la sincronía.',
    ],
    enlaces: [{ to: '/signage/plantillas', t: 'Plantillas' }],
  },
  {
    q: '¿Tengo una pantalla vertical (9:16)?',
    pasos: [
      'Al crear la pantalla elige orientación "vertical": el diseño se reacomoda solo (hero apilado, carta a 1 columna, tipografía mayor).',
      'Si el box gira el panel pero entrega imagen horizontal, el reproductor rota el contenido automáticamente.',
    ],
    enlaces: [{ to: '/signage/pantallas', t: 'Pantallas' }],
  },
  {
    q: '¿Cómo hago que la pantalla reaccione al clima, a las ventas o a la agenda médica?',
    pasos: [
      'CLIMA: en Plantillas, cada escena tiene el selector "Clima" — ej. una escena de imagen/mensaje de antigripales con "Solo si llueve": entra sola cuando llueve o hay >60% de probabilidad en las próximas 3 horas (Open-Meteo, con las coordenadas del local).',
      'VENTAS: la escena "Lo más llevado HOY" muestra los productos más comprados hoy en ESTE local con su contador — se alimenta sola del POS.',
      'AGENDA: la escena "Turnos de hoy" lista los médicos con cupos libres hoy y su próximo horario; se recalcula al agendar o cancelar citas.',
      'CUENTA REGRESIVA: el feed "Cuenta regresiva" ahora puede atarse a un COMBO — termina cuando vence el combo, sin escribir la fecha dos veces.',
      'STOCK: no hay nada que configurar — un producto agotado desaparece de la pantalla en ~20 s y vuelve al reponerlo.',
    ],
    enlaces: [{ to: '/signage/plantillas', t: 'Plantillas' }, { to: '/signage/combos', t: 'Combos' }],
  },
  {
    q: '¿Cómo muestro mis redes sociales en la pantalla?',
    pasos: [
      'Signage → Redes sociales: crea la cuenta (red + usuario). Empieza en modo CURADO: tú cargas los posts a mano (imagen de la Biblioteca + texto + likes opcionales) — control total de lo que se exhibe.',
      'Agrega la escena "Redes sociales" a una plantilla (puedes filtrar por red) y publica: cada post rota como una tarjeta con el handle, la foto, el texto y el botón "Síguenos".',
      'FIJA un post para que salga siempre primero, u OCÚLTALO sin borrarlo (el interruptor de la tarjeta).',
      'Sincronización automática (opcional): cambia la cuenta a modo API — Instagram Business o página de Facebook — y carga el token de Meta con el botón 🔑 (queda cifrado). El sistema baja los últimos posts solo, según el intervalo. TikTok y X por ahora solo en modo curado.',
      'El token de Meta dura ~60 días: registra su vencimiento y el sistema te avisa en esta pantalla cuando esté por caducar.',
    ],
    enlaces: [{ to: '/signage/social', t: 'Redes sociales' }, { to: '/signage/plantillas', t: 'Plantillas' }, { to: '/signage/medios', t: 'Biblioteca' }],
  },
  {
    q: '¿Cómo agrego gadgets (clima, UV, saludo, cuenta regresiva) a mis pantallas?',
    pasos: [
      'Los gadgets son del LUGAR, no del contenido: se configuran en el GRUPO (todas sus pantallas los heredan) o en la PANTALLA (override individual) — no en la plantilla, así sobreviven a la programación por horario y a las franjas vendidas.',
      'En el header: ☀ CLIMA (temperatura + icono del local, con sus coordenadas) y la píldora UV, que solo aparece cuando es accionable (UV≥6, de 10:00 a 16:00) — ideal para empujar protector solar.',
      'Chips sobre el contenido (máx. 3): SALUDO de la campaña vigente (🎄 Felices fiestas), CUENTA REGRESIVA (a una fecha o al vencimiento de un combo) y TEXTO del local ("Abierto hasta las 22:00"). Eliges la posición: arriba centro, abajo izquierda o derecha.',
      'El reloj ya vive en el header (junto a EN VIVO) — no es un gadget aparte.',
      'Se ocultan solos en video y en publicidad pagada (el anunciante recibe la pantalla limpia); configurable.',
      'Los cambios aplican en las pantallas en ~20 segundos, sin re-publicar plantillas.',
      'Extras opcionales: FECHA corta junto al reloj (apagada por defecto), y en Vista previa el check "Gadgets de ejemplo" te muestra la capa completa con cada tema y en vertical, sin configurar una pantalla real.',
    ],
    enlaces: [{ to: '/signage/grupos', t: 'Grupos' }, { to: '/signage/pantallas', t: 'Pantallas' }, { to: '/signage/preview', t: 'Vista previa' }],
  },
  {
    q: '¿Cómo pongo TV en vivo o un stream en la pantalla?',
    pasos: [
      'En Plantillas, agrega una escena de VIDEO y pega la URL del stream HLS (.m3u8) — por ejemplo tu canal corporativo o una señal contratada.',
      'El reproductor lo mantiene vivo solo: si el stream se corta o se congela, se reconecta automáticamente (watchdog). La duración de la escena es el tope de permanencia antes de pasar a la siguiente.',
      'Para el formato "CNN" (TV en una zona + catálogo al lado), usa la escena de DISEÑO POR ZONAS con el video en una zona.',
      'Siempre sin audio (como todo el reproductor). Puedes probar una URL en Vista previa antes de publicar.',
      '⚠ LEGAL: retransmitir TV o música comercial en un local es comunicación pública y requiere licencias (SAYCE/SARIME/ASOPROFON en Ecuador). Usa señales propias o con derechos; las licencias de señales de terceros corren por cuenta del local.',
    ],
    enlaces: [{ to: '/signage/plantillas', t: 'Plantillas' }, { to: '/signage/preview', t: 'Vista previa' }],
  },
  {
    q: '¿Cómo recibo contenido de mi agencia o de un laboratorio sin darles acceso al sistema?',
    pasos: [
      'Buzón externo → "Carpeta": registra la carpeta de Google Drive (compártela como Lector con el email del service account) o Dropbox del proveedor, y carga la credencial con 🔑 (queda cifrada).',
      'Ellos trabajan en su entorno de siempre: sueltan imágenes o videos en la carpeta y en minutos aparecen aquí como PENDIENTES.',
      'Un administrador APRUEBA o RECHAZA cada archivo con preview — nada llega a las pantallas sin ese visto bueno. Imagen aprobada → Biblioteca al instante; video aprobado → se publica en el próximo ciclo.',
      'Desde la Biblioteca, el contenido se usa en escenas imagen/video como cualquier otro medio.',
      'Relacionado: ACTIVAR un contrato de franja (la pauta sale en pantalla) ahora también exige un administrador — aprobación editorial formal de la creatividad.',
    ],
    enlaces: [{ to: '/signage/buzon', t: 'Buzón externo' }, { to: '/signage/medios', t: 'Biblioteca' }, { to: '/signage/contratos', t: 'Contratos' }],
  },
  {
    q: '¿Cómo hago que las ofertas salgan solas en quincena y fin de mes?',
    pasos: [
      'Arma la plantilla de ofertas fuertes (combos, liquidaciones) y publícala.',
      'En Programación crea la regla apuntando a esa plantilla y en "Momento" elige "Días de pago" (14-16 y fin de mes), solo quincena o solo fin de mes. La regla se activa sola cada mes y vence a la programación normal esos días.',
      'Opcional: crea una Campaña con momento "Días de pago" y un saludo ("¡Llegó la quincena!") con rango de fechas amplio — el efecto visual aparece solo esos días, sin agendarlo nunca más.',
    ],
    enlaces: [{ to: '/signage/programacion', t: 'Programación' }, { to: '/signage/campanas', t: 'Campañas' }],
  },
  {
    q: '¿Cómo publica el gerente un aviso rápido en las pantallas de su local?',
    pasos: [
      'En Avisos del local escribe el texto (máx. 140), elige el local y la vigencia — sale en el ticker de las pantallas en menos de 20 segundos, destacado con 📢.',
      'No necesita ser administrador: basta estar asignado a una caja de esa sucursal (el mismo permiso que lo deja operar el POS). Solo puede publicar y operar pantallas de SUS locales.',
      'El aviso caduca solo al vencer la vigencia, o se retira antes con un clic. Desde la misma pantalla también puede refrescar sus pantallas y capturar lo que muestran (con Fully Kiosk).',
    ],
    enlaces: [{ to: '/signage/avisos', t: 'Avisos del local' }],
  },
  {
    q: '¿Cómo configuro Fully Kiosk en el box (captura, reinicio remoto, salud)?',
    pasos: [
      'Instala "Fully Kiosk Browser" en el box Android (Play Store; la licencia PLUS por equipo desbloquea el reboot remoto y el arranque robusto).',
      'En Fully → Web Content Settings pon como Start URL la URL de la pantalla (Pantallas → copiar URL).',
      'Activa: Web Auto Reload al recuperar red, Launch on Boot, Keep Screen On, y en Advanced Web Settings habilita "Enable JavaScript Interface" (sin esto no hay captura ni salud del box).',
      'Activa Kiosk Mode con PIN para que nadie salga del reproductor.',
      'Listo: en Pantallas verás la etiqueta "Fully" con batería/wifi/memoria en cada latido, y los botones de capturar pantalla (foto REAL de lo que se ve), reiniciar la app y reiniciar el equipo.',
      'Sin Fully todo sigue funcionando; esos tres comandos responden "no soportado".',
    ],
    enlaces: [{ to: '/signage/pantallas', t: 'Pantallas' }],
  },
  {
    q: '¿Funciona sin internet?',
    pasos: [
      'Sí: tras la primera carga, la app queda cacheada y la pantalla sigue reproduciendo el último contenido si la red cae (aparece "Sin conexión").',
      'Al reconectar, se actualiza sola si cambió algo. Requiere HTTPS (o localhost) para el modo offline.',
    ],
    enlaces: [],
  },
  {
    q: '¿Qué hardware necesito?',
    pasos: [
      'Un box/stick Android barato conectado por HDMI, con un navegador en modo kiosko y arranque automático.',
      'Apple TV NO sirve (tvOS no tiene navegador ni PWA): ponle un stick Android a esa pantalla.',
    ],
    enlaces: [],
  },
];

const GLOSARIO: { t: string; d: string }[] = [
  { t: 'Pantalla / token', d: 'Cada display tiene una URL única (token). Es la única llave del reproductor; se puede regenerar.' },
  { t: 'Plantilla (playlist)', d: 'Secuencia de escenas que se reproduce en bucle en la pantalla.' },
  { t: 'Escena', d: 'Un tipo de contenido de la playlist. Del catálogo: hero, marco roto (pop-out 3D), carta, promociones, más vendidos, lo más llevado hoy, lentos, por vencer, combos, cross-sell. De contexto: mensaje, imagen, video/playlist, QR, feeds (reloj/clima/cuenta/noticias), salud del día, redes sociales, reseñas de pacientes, turnos de hoy, y layouts por zonas.' },
  { t: 'Dayparting', d: 'Programación por franja horaria: qué plantilla corre y cuándo. Tapa a la plantilla por defecto solo dentro de sus franjas.' },
  { t: 'Plantilla por defecto', d: 'La plantilla asignada a la Pantalla; se muestra siempre que NO haya una franja de programación (ni experimento) vigente.' },
  { t: 'Grupo', d: 'Conjunto de pantallas (por zona/ciudad/tipo) para asignarles contenido en conjunto.' },
  { t: 'Tema / brand kit', d: 'Paleta de colores y logo que visten el reproductor.' },
  { t: 'Campaña', d: 'Capa de efecto estacional (Navidad, Carnaval, Black Friday…) que se monta sobre el catálogo por fecha.' },
  { t: 'Proof-of-play', d: 'Evidencia de qué se mostró, cuándo y en qué pantalla (base del Resumen, del uplift y del A/B).' },
  { t: 'Versión / publicar', d: 'Cada publicación de una plantilla queda versionada; el reproductor usa la última publicada y puedes revertir.' },
  { t: 'Transición', d: 'Efecto de entrada entre escenas: fundido, deslizar, zoom o corte (se elige por escena).' },
  { t: 'Marco roto', d: 'Escena pop-out "3D": el producto arranca dentro de un marco dibujado, lo rompe y levita con sombra. El fondo de la foto se recorta automáticamente (foto de estudio); precio/stock EN VIVO del local.' },
  { t: 'Faststart', d: 'MP4 con su índice al inicio; necesario para que el video autoreproduzca en la pantalla.' },
  { t: 'Punto focal', d: 'Punto de una imagen que se mantiene centrado al recortar a pantalla completa.' },
  { t: 'Feed', d: 'Widget de escena: reloj, clima (sin claves) o noticias (RSS que baja el sistema).' },
  { t: 'Combo / kit', d: 'Pack curado con descuento; escena "Combo". Al guardarlo crea automáticamente su promoción POS: la pantalla muestra EL PRECIO QUE COBRA LA CAJA, y sin promo o con ítem agotado no se exhibe.' },
  { t: 'Por vencer', d: 'Escena con los lotes próximos a caducar del local (días restantes). Solo muestra la rebaja REAL aplicada (markdown de Inventario) — jamás un precio que la caja no cobre.' },
  { t: 'Coherencia pantalla↔caja', d: 'Principio del módulo: lo que la pantalla promete, la caja lo cobra (el precio exhibido obliga — LODC). El botón "Validar precios" del Resumen revisa que combos y promos POS estén alineados.' },
  { t: 'Cupón canjeable', d: 'Código corto exhibido en pantalla que el cliente dice en caja; el POS lo valida y canjea atómicamente al cobrar (vigencia con hora, local, tope de canjes). Da atribución DIRECTA pantalla→venta.' },
  { t: 'Pantalla de caja', d: 'Pantalla en modo "caja": muestra al cliente su compra en curso (líneas, total, QR De Una, vuelto) y, ociosa, la playlist normal. Sin datos personales del cliente (LOPDP).' },
  { t: 'Certificado de reproducción', d: 'Documento con folio verificable en línea (QR) que congela las métricas de emisión de un contrato: reproducciones, tiempo, pantallas y % de cumplimiento. Respaldo formal para agencias/anunciantes.' },
  { t: 'Avails', d: 'Inventario publicitario disponible: qué pantallas y franjas están libres para vender en un rango de fechas. El cotizador los muestra con audiencia (tickets de caja) y tarifa sugerida.' },
  { t: 'Fully Kiosk', d: 'App Android que corre el reproductor en modo kiosko. Con su JS Interface habilitada, el sistema recibe la salud del box (batería, wifi, memoria) y puede capturar la pantalla o reiniciar app/equipo a distancia.' },
  { t: 'Aviso del local', d: 'Mensaje corto que el encargado publica al ticker de las pantallas de su sucursal, con vigencia por horas. El permiso viene de su asignación de caja del POS — no requiere administrador.' },
  { t: 'Días de pago', d: 'Ventanas recurrentes del calendario comercial: quincena (14-16) y fin de mes (últimos 2 días + 1 y 2). Una regla de programación o campaña con "momento" se activa sola cada mes en esas fechas.' },
  { t: 'CPM efectivo', d: 'Costo por cada 1.000 personas expuestas. Aquí la audiencia es el tráfico de caja del local dentro de la franja (auditable en el POS), no una estimación de tránsito.' },
  { t: 'Salud del día', d: 'Escena con un consejo de salud + clima/UV del local que sugiere un producto según el día (UV alto→protector, frío→antigripal…).' },
  { t: 'Muro social', d: 'Escena "Redes sociales": posts de Instagram/Facebook/TikTok/X como tarjetas. Curado = los cargas tú; API = se sincronizan solos (Meta, token cifrado).' },
  { t: 'Lo más llevado HOY', d: 'Escena con los productos más comprados hoy en este local y su contador ("N personas lo llevaron hoy") — presión social en vivo desde el POS.' },
  { t: 'Turnos de hoy', d: 'Escena con los médicos que tienen cupos libres HOY (horarios − citas − ausencias) y su próximo horario — captación de citas/teleconsulta en sala de espera.' },
  { t: 'Condición de clima', d: 'Cualquier escena puede mostrarse solo si llueve (o lloverá en 3h), hace frío, calor o hay UV alto — ej. lluvia → escena de antigripales. Se configura por escena en Plantillas.' },
  { t: 'Stock a cero', d: 'Un producto agotado en el local desaparece de la pantalla en ~20 segundos y regresa solo al reponer stock.' },
  { t: 'Buzón externo', d: 'Carpeta Drive/Dropbox vigilada: lo que sube la agencia/el proveedor entra a moderación (aprobar/rechazar con preview) antes de tocar la Biblioteca.' },
  { t: 'Reseñas de pacientes', d: 'Escena con las calificaciones internas 4-5★ del portal (promedio + comentarios); el autor sale solo con iniciales (LOPDP).' },
  { t: 'Playlist de YouTube', d: 'La escena de video acepta la URL de una playlist (list=…) y la reproduce en secuencia, siempre en silencio.' },
  { t: 'Gadgets persistentes', d: 'Datos siempre visibles mientras el contenido rota: clima y UV en el header, y chips discretos (saludo de campaña, cuenta regresiva, texto del local). Se configuran por grupo o por pantalla y se apagan solos en la publicidad pagada.' },
  { t: 'Stream HLS (TV en vivo)', d: 'La escena de video acepta streams .m3u8 (canal corporativo, señal propia) con reconexión automática. ⚠ Retransmitir TV/música comercial en un local es comunicación pública y requiere licencias (SAYCE/SARIME/ASOPROFON) — úsalo solo con señales propias o con derechos.' },
  { t: 'Regla de realce', d: 'Etiqueta promocional (OFERTA, HORA FELIZ…) que se pinta sobre un producto según una condición.' },
  { t: 'Retail media', d: 'Venta de espacio a marcas: anuncios con aprobación, frecuencia (SOV) y reporte de impresiones.' },
  { t: 'Franja vendida (contrato)', d: 'Una franja horaria comprada por un anunciante: su plantilla tiene prioridad máxima en esa hora y se factura al SRI al cliente del anunciante.' },
  { t: 'Cross-sell / afinidad', d: 'Recomendaciones "también se llevan…" en la pantalla según el carrito en curso de la caja, calculadas del historial de ventas (market-basket). Carrito anónimo, con caducidad (LOPDP).' },
  { t: 'QR phygital', d: 'QR en la pantalla que lleva al cliente a comprar el producto en línea o a la tienda.' },
  { t: 'Uplift', d: 'Efecto observado del contenido en las ventas, cruzando exhibición con ventas del local.' },
  { t: 'A/B testing (switchback)', d: 'Comparar dos plantillas alternándolas por día en un grupo para ver cuál rinde más.' },
  { t: 'Emparejamiento', d: 'Vincular un box a una pantalla con un código corto (sin token) y habilitar control remoto.' },
];

// ---------------------------------------------------------------- APK TV Box

// El APK vive en <servidor>/tv/ (lo publica signage-tv/build.sh dentro del dist
// del frontend). BASE_URL cubre la diferencia local (/erp/) vs remoto (/).
const BASE_TV = `${import.meta.env.BASE_URL.replace(/\/$/, '')}/tv`;

const APK_PASOS: { t: string; d: React.ReactNode }[] = [
  { t: 'Permitir orígenes desconocidos', d: <>En el TV box: Ajustes → Seguridad → <strong>orígenes desconocidos</strong> (o "instalar apps desconocidas" para el navegador/Downloader que uses en el paso 2).</> },
  { t: 'Descargar e instalar el APK', d: <>Abre el navegador del box (o la app <strong>Downloader</strong>, estándar en TV boxes) y entra a la URL de descarga de abajo → instalar.</> },
  { t: 'Configurar el servidor', d: <>Abre <strong>K²E Signage</strong>. La primera vez pide la <strong>URL del servidor</strong> (p. ej. <code className="text-xs">https://205.235.2.197</code>) — escríbela con el control y Guardar.</> },
  { t: 'Fijarla como launcher (el paso del kiosko)', d: <>Android pregunta qué <strong>aplicación de inicio</strong> usar → elige K²E Signage / "Siempre". Con eso el box <strong>enciende directo en la cartelera</strong> y el botón Home vuelve a ella.</> },
  { t: 'Vincular la pantalla', d: <>Aparece el código de 6 dígitos → ingrésalo en <strong>Signage → Pantallas → Vincular pantalla</strong>. El pairing es una sola vez: el token queda guardado en el box y en cada encendido entra solo al reproductor.</> },
];

const APK_FUNC: { t: string; d: string }[] = [
  { t: 'Kiosko real', d: 'Como launcher (HOME), el box arranca directo en la cartelera; BACK no sale de la app y Home vuelve a ella.' },
  { t: 'Pairing persistente', d: 'El token de la pantalla persiste entre reinicios (localStorage del WebView): se vincula una sola vez.' },
  { t: 'Watchdog', d: 'Sin conexión muestra un aviso y reintenta cada 10 s; si el motor web del box se cae (poca RAM), se recrea solo sin dejar la pantalla congelada.' },
  { t: 'Auto-actualización', d: 'Cada 6 h consulta la versión publicada en el servidor; si hay una nueva, la descarga y pide confirmar la instalación con OK en el control.' },
  { t: 'Pantalla siempre encendida', d: 'Mantiene la pantalla activa (keep screen on) mientras la app está al frente.' },
  { t: 'Certificados propios', d: 'Acepta el certificado self-signed del servidor configurado (solo de ese host, no de cualquiera).' },
];

const APK_PROBLEMAS: { q: string; r: React.ReactNode }[] = [
  { q: 'No aparece el código de pairing', r: <>El box no llega al servidor: revisa la red del box y que la URL configurada sea la correcta (con https:// y sin / final). Abre la configuración con la tecla <strong>MENU</strong> del control o <strong>7 pulsaciones rápidas de BACK</strong>.</> },
  { q: 'El video o las animaciones se ven lentos', r: <>Depende de la versión de <em>Android System WebView</em> del box. Si el box tiene Play Store, actualízalo desde ahí. Para compras nuevas prefiere boxes con Android 10 o superior.</> },
  { q: 'No arranca solo al encender el TV', r: <>En Android 10+ el arranque automático solo es confiable en <strong>modo launcher</strong> (paso 4). Verifica que K²E Signage sea la aplicación de inicio predeterminada.</> },
  { q: 'La actualización no llega', r: <>El ciclo corre cada 6 h — como máximo ese es el retraso. La primera vez Android pide habilitar "instalar apps desconocidas" para K²E Signage: hay que aceptarlo una vez con el control.</> },
  { q: 'Quiero salir del kiosko / usar el box para otra cosa', r: <>Abre la configuración (MENU o 7×BACK) → Cancelar, y en Ajustes de Android cambia la aplicación de inicio predeterminada al launcher original.</> },
];

function AyudaApkTv() {
  const ir = useIr();
  const urlApk = `${window.location.origin}${BASE_TV}/signage-tv.apk`;

  // Versión publicada EN VIVO en este servidor (misma filosofía del checklist)
  const { data: ver } = useQuery<{ versionCode: number; versionName: string } | null>({
    queryKey: ['sig-apk-version'],
    queryFn: () => fetch(`${BASE_TV}/version.json`, { cache: 'no-store' }).then((r) => (r.ok ? r.json() : null)).catch(() => null),
    staleTime: 60 * 1000,
  });

  const copiar = async () => {
    try { await navigator.clipboard.writeText(urlApk); toast.success('URL copiada'); }
    catch { toast.error('No se pudo copiar'); }
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><MonitorSmartphone className="w-4 h-4" /> Qué es</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            La app Android <strong>K²E Signage</strong> convierte cualquier TV box genérico (Android 5 o superior) en una pantalla
            de cartelería en <strong>modo kiosko</strong>: se instala una vez, se vincula con el código de pairing y desde ahí el
            equipo enciende directo en el reproductor, sin escritorio de Android ni intervención de nadie. Reemplaza el paso de
            "abrir la URL en el navegador del box" del flujo básico.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {APK_FUNC.map((f) => (
              <div key={f.t} className="border rounded-md p-3">
                <div className="text-sm font-medium">{f.t}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{f.d}</div>
              </div>
            ))}
          </div>
          <div className="border rounded-md p-3 bg-muted/30">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1"><Download className="w-3.5 h-3.5" /> URL de descarga (este servidor)</div>
            <div className="flex flex-wrap items-center gap-2">
              <code className="text-xs break-all">{urlApk}</code>
              <button className="inline-flex items-center gap-1 text-primary hover:underline text-xs" onClick={() => void copiar()}>
                <Copy className="w-3.5 h-3.5" /> Copiar
              </button>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {ver
                ? <>Versión publicada aquí: <strong>v{ver.versionName}</strong> (código {ver.versionCode}).</>
                : <span className="text-amber-600">El APK aún no está publicado en este servidor (falta /tv/signage-tv.apk — avisa a TI).</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Instalación en el TV box (~3 minutos)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {APK_PASOS.map((p, i) => (
            <div key={p.t} className="flex items-start gap-3 text-sm">
              <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">{i + 1}</div>
              <div>
                <span className="font-medium">{p.t}.</span>{' '}
                <span className="text-muted-foreground">{p.d}</span>
              </div>
            </div>
          ))}
          <div className="text-sm pt-1">
            <button className="inline-flex items-center gap-1 text-primary hover:underline" onClick={() => ir('/signage/pantallas', 'Pantallas')}>
              <Link2 className="w-3.5 h-3.5" /> Pantallas (vincular con el código)
            </button>
          </div>
          <p className="text-xs text-muted-foreground border-t pt-2">
            Para reabrir la configuración en el box (cambiar el servidor): tecla <strong>MENU</strong> del control o <strong>7 pulsaciones rápidas de BACK</strong>.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Actualizaciones (cómo se publica una versión nueva)</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            Las TVs se actualizan <strong>solas</strong>: cada 6 horas consultan <code className="text-xs">{'<servidor>'}/tv/version.json</code> y,
            si hay una versión con código mayor a la instalada, descargan el APK nuevo y piden confirmar con OK en el control.
            No hay que tocar los equipos.
          </p>
          <p>
            <strong>Para TI:</strong> el proyecto vive en <code className="text-xs">signage-tv/</code> del repositorio. Publicar = subir
            <code className="text-xs"> versionCode</code>/<code className="text-xs">versionName</code> en <code className="text-xs">app/build.gradle</code>,
            correr <code className="text-xs">./build.sh</code> (compila, firma y deja el APK + version.json en <code className="text-xs">frontend/public/tv/</code>)
            y desplegar el frontend como siempre — el APK viaja dentro del dist a todos los servidores. El keystore de firma está fuera del
            repositorio y <strong>debe respaldarse</strong>: sin él las TVs rechazan la actualización (firma distinta). Detalle completo en{' '}
            <code className="text-xs">signage-tv/README.md</code>.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Solución de problemas</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {APK_PROBLEMAS.map((p) => (
            <Acordeon key={p.q} q={p.q}>
              <p>{p.r}</p>
            </Acordeon>
          ))}
        </CardContent>
      </Card>
    </>
  );
}

function Acordeon({ q, children }: { q: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-md">
      <button className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-left" onClick={() => setOpen((o) => !o)}>
        {q} {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
      </button>
      {open && <div className="px-3 pb-3 text-sm text-muted-foreground">{children}</div>}
    </div>
  );
}

export function AyudaSignage() {
  const ir = useIr();
  const [tab, setTab] = useState<'guia' | 'apk'>('guia');
  const { data, isFetching } = useQuery<AyudaData>({
    queryKey: ['sig-ayuda'], queryFn: () => sigRpc<AyudaData>('sig_ayuda_estado'),
    staleTime: 0, refetchOnMount: 'always',
  });

  const Ir = ({ to, t }: { to: string; t: string }) => (
    <button className="inline-flex items-center gap-1 text-primary hover:underline" onClick={() => ir(to, t)}>
      <Link2 className="w-3.5 h-3.5" /> {t}
    </button>
  );

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <h1 className="text-xl font-semibold flex items-center gap-2">
        <LifeBuoy className="w-5 h-5" /> Ayuda — Digital Signage
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </h1>

      <div className="flex items-center gap-2 border-b">
        {([['guia', 'Guía del módulo'], ['apk', 'APK TV Box (kiosko)']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'apk' ? <AyudaApkTv /> : (<>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Listo para operar</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {CHECKS.map((c) => {
            const ok = Boolean(data?.checklist?.[c.key]);
            return (
              <div key={c.key} className="flex items-start gap-2 text-sm">
                {ok
                  ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600 shrink-0" />
                  : <XCircle className={`w-4 h-4 mt-0.5 shrink-0 ${c.advisory ? 'text-amber-500' : 'text-red-500'}`} />}
                <div>
                  <span className={ok ? '' : 'font-medium'}>{c.label}</span>
                  <span className="text-muted-foreground"> — {c.ayuda}</span>{' '}
                  {!ok && <Ir to={c.ruta} t={c.titulo} />}
                </div>
              </div>
            );
          })}
          {data && (
            <div className="text-xs text-muted-foreground border-t pt-2 mt-2">
              {data.kpis.pantallas} pantalla(s) · {data.kpis.online} en línea · {data.kpis.plantillas} plantilla(s) ·
              {' '}{data.kpis.temas} tema(s) · {data.kpis.repro_24h} reproducción(es) 24h
              {data.kpis.campana_hoy ? ` · campaña hoy: ${data.kpis.campana_hoy}` : ' · sin campaña hoy'}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">El flujo para montar una pantalla</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {FLUJO.map((f) => (
            <div key={f.n} className="flex items-start gap-3 text-sm">
              <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">{f.n}</div>
              <div>
                <span className="font-medium">{f.titulo}.</span>{' '}
                <span className="text-muted-foreground">{f.detalle}</span>{' '}
                <Ir to={f.ruta} t={f.tituloTab} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">¿Qué plantilla se muestra y cuándo?</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>El reproductor decide la plantilla del momento en este orden — gana el <strong>primero</strong> que aplique:</p>
          <ol className="list-decimal ml-5 space-y-1">
            <li><span className="font-medium">Franja vendida (contrato de anunciante)</span>: si una marca compró esta franja horaria (Retail media → Contratos), su plantilla <em>manda sobre todo lo demás</em> — es el compromiso comercial.</li>
            <li><span className="font-medium">Experimento A/B</span> (si hay uno activo sobre el grupo): manda sobre la programación y la por defecto.</li>
            <li><span className="font-medium">Programación (franja horaria)</span>: si hay una franja vigente para esta pantalla o su grupo, hoy y a esta hora.</li>
            <li><span className="font-medium">Plantilla por defecto de la Pantalla</span>: el respaldo — lo que se ve cuando ninguna franja aplica.</li>
          </ol>
          <p className="text-muted-foreground">
            <strong>Idea clave:</strong> la <strong>Pantalla</strong> define "lo que se muestra normalmente"; la <strong>Programación</strong> tapa esa por defecto <em>solo</em> dentro de sus franjas. <strong>Se complementan</strong>: fuera de las franjas siempre vuelve a la por defecto. La plantilla por defecto es obligatoria; la programación es opcional.
          </p>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              Ejemplo — Pantalla con por-defecto <em>“Catálogo”</em> y una franja <em>12:00–15:00 “Menú a la carta”</em>
            </div>
            <div className="border rounded-md divide-y overflow-hidden">
              {[
                { h: '00:00 – 12:00', p: 'Catálogo', y: 'por defecto de la Pantalla', hl: false },
                { h: '12:00 – 15:00', p: 'Menú a la carta', y: 'Programación (tapa la por defecto)', hl: true },
                { h: '15:00 – 24:00', p: 'Catálogo', y: 'por defecto (terminó la franja)', hl: false },
              ].map((r) => (
                <div key={r.h} className={`flex items-center gap-3 px-3 py-1.5 ${r.hl ? 'bg-primary/5' : ''}`}>
                  <span className="font-mono text-xs tabular-nums w-28 shrink-0 text-muted-foreground">{r.h}</span>
                  <span className="font-medium">{r.p}</span>
                  <span className="text-xs text-muted-foreground ml-auto">{r.y}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              Ejemplo 2 — Un día completo con las 3 capas (por defecto · programación · franja vendida)
            </div>
            <p className="text-muted-foreground mb-2">
              Una tienda arma su día con <strong>Programación</strong> (una plantilla por bloque) y le <strong>vende a un laboratorio</strong> la franja 13:00–14:00 (Retail media → Contratos). Así se ve la parrilla — fíjate que la <span className="text-amber-600 font-medium">franja vendida</span> le gana a la programación de esa hora, y fuera de todos los bloques vuelve la por defecto:
            </p>
            <div className="border rounded-md divide-y overflow-hidden">
              {[
                { h: '08:00 – 12:00', p: 'Promociones', y: 'Programación', o: 'prog' as const },
                { h: '12:00 – 13:00', p: 'Noticias (formato CNN)', y: 'Programación', o: 'prog' as const },
                { h: '13:00 – 14:00', p: 'Pauta Laboratorio X', y: 'Franja VENDIDA — gana a la programación', o: 'vendida' as const },
                { h: '14:00 – 16:00', p: 'Más vendidos', y: 'Programación', o: 'prog' as const },
                { h: '16:00 – 18:00', p: 'Redes sociales (escena social: posts de Instagram/Facebook)', y: 'Programación', o: 'prog' as const },
                { h: '18:00 – 23:00', p: 'Cross-sell (según el carrito del POS)', y: 'Programación', o: 'prog' as const },
                { h: '23:00 – 08:00', p: 'Promociones', y: 'por defecto (ningún bloque aplica)', o: 'def' as const },
              ].map((r) => (
                <div key={r.h} className={`flex items-center gap-3 px-3 py-1.5 ${r.o === 'vendida' ? 'bg-amber-500/10' : r.o === 'prog' ? 'bg-primary/5' : ''}`}>
                  <span className="font-mono text-xs tabular-nums w-28 shrink-0 text-muted-foreground">{r.h}</span>
                  <span className={`font-medium ${r.o === 'vendida' ? 'text-amber-700 dark:text-amber-400' : ''}`}>{r.p}</span>
                  <span className="text-xs text-muted-foreground ml-auto text-right">{r.y}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Todo esto se configura sin tocar la Pantalla: los 6 bloques en <strong>Programación</strong>, y la venta de la franja en <strong>Contratos</strong> (que además puede <strong>facturarse al anunciante</strong> con la impresión que reporta el sistema). El <strong>Cross-sell</strong> de la última franja lee el carrito en curso de la caja del local y sugiere "también se llevan…" en tiempo real.
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            Si dos franjas coinciden, gana la de ámbito <strong>Pantalla</strong> sobre la de <strong>Grupo</strong>; entre iguales, la de mayor <strong>prioridad</strong>. Los cambios se reflejan solos en ≤20 s (no hay que limpiar caché). Regenerar el token invalida la URL anterior: abre la nueva en el box.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Capacidades avanzadas</CardTitle></CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-2">
          {AVANZADO.map((a) => (
            <button key={a.ruta + a.t} className="text-left border rounded-md p-3 hover:bg-muted/50 transition" onClick={() => ir(a.ruta, a.tab)}>
              <div className="text-sm font-medium flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5 text-primary shrink-0" /> {a.t}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{a.d}</div>
            </button>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">¿Cómo hago…?</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {COMO.map((c) => (
            <Acordeon key={c.q} q={c.q}>
              <ol className="list-decimal ml-5 space-y-1">
                {c.pasos.map((p, i) => <li key={i}>{p}</li>)}
              </ol>
              {c.enlaces.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-2">
                  {c.enlaces.map((e) => <Ir key={e.to} to={e.to} t={e.t} />)}
                </div>
              )}
            </Acordeon>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Glosario</CardTitle></CardHeader>
        <CardContent className="grid gap-2 md:grid-cols-2">
          {GLOSARIO.map((g) => (
            <div key={g.t} className="text-sm"><span className="font-medium">{g.t}:</span> <span className="text-muted-foreground">{g.d}</span></div>
          ))}
        </CardContent>
      </Card>

      </>)}
    </div>
  );
}
